import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Timeline } from "primereact/timeline";
import {
  EssCard,
  CommentThread,
  StatusTag,
  PriorityTag,
} from "./components/helper";
import { Divider } from "primereact/divider";
import { STATUS_OPT, PRIORITY_OPT } from "./data/constants";
import { supportApi } from "../../services";

const AgentWork = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from");
  const backPath =
    fromParam === "admin" ? `/support-admin?tab=${searchParams.get("tab") || "0"}` : "/support-queue";
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [reply, setReply] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [status, setStatus] = useState(null);
  const [priority, setPriority] = useState(null);
  const [assignedTo, setAssignedTo] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [subcategoryId, setSubcategoryId] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [holdReason, setHoldReason] = useState("");
  const fileInputRef = useRef(null);
  const [allUsers, setAllUsers] = useState([]);
  const [approvalUserIds, setApprovalUserIds] = useState([]);
  const [approvalComment, setApprovalComment] = useState("");
  const [sendingApproval, setSendingApproval] = useState(false);
  useEffect(() => {
    supportApi.getActiveUsers()
      .then((r) => {
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        setAllUsers(list.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })));
      })
      .catch(() => {});
  }, []);

  const onRequestApproval = async () => {
    if (!approvalUserIds.length || !approvalComment.trim()) {
      toast.warn("Please select at least one user and enter a comment");
      return;
    }
    setSendingApproval(true);
    try {
      await supportApi.requestApproval(id, { userIds: approvalUserIds, comment: approvalComment.trim() });
      setApprovalUserIds([]);
      setApprovalComment("");
      const [cRes, actRes] = await Promise.all([
        supportApi.getComments(id),
        supportApi.getActivityLog(id),
      ]);
      setComments(Array.isArray(cRes.data?.data) ? cRes.data.data : []);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Approval request sent successfully");
    } catch {
      toast.error("Failed to send approval request");
    } finally {
      setSendingApproval(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([
      supportApi.getTicketById(id),
      supportApi.getComments(id),
      supportApi.getActivityLog(id),
      supportApi.getAttachments(id),
    ])
      .then(async ([t, c, act, att]) => {
        const td = t.data?.data ?? t.data ?? {};
        setTicket(td);
        setComments(Array.isArray(c.data?.data) ? c.data.data : []);
        setActivityLog(Array.isArray(act.data?.data) ? act.data.data : []);
        setAttachments(Array.isArray(att.data?.data) ? att.data.data : []);
        setStatus(td.status ?? null);
        setPriority(td.priority ?? null);
        setAssignedTo(td.assignedTo ?? null);

        const deptId = td.departmentId ?? td.category?.departmentId ?? null;
        setDepartmentId(deptId);

        // Load categories and subcategories upfront so dropdowns have options when values are set
        if (deptId) {
          const catRes = await supportApi.getCategories({ departmentId: deptId }).catch(() => null);
          const catList = Array.isArray(catRes?.data?.data) ? catRes.data.data : [];
          setCategories(catList.map((c) => ({ label: c.name, value: c.id })));
        }
        if (td.categoryId) {
          const subRes = await supportApi.getSubCategories(td.categoryId).catch(() => null);
          const subList = Array.isArray(subRes?.data?.data) ? subRes.data.data : [];
          setSubCategories(subList.map((s) => ({ label: s.name, value: s.id })));
        }

        setCategoryId(td.categoryId ?? null);
        setSubcategoryId(td.subcategoryId ?? null);
      })
      .catch(() => toast.error("Failed to load ticket"));
  }, [id]);

  // Load departments list once on mount
  useEffect(() => {
    supportApi.getDepartments()
      .then((d) => {
        const list = Array.isArray(d.data?.data) ? d.data.data : [];
        setDepartments(list.map((dep) => ({ label: dep.title, value: dep.id })));
      })
      .catch(() => {});
  }, []);

  const onSendReply = async (andResolve = false) => {
    if (!reply.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      if (reply.trim()) {
        const res = await supportApi.addComment(id, {
          message: reply,
          isInternalNote: isInternal,
        });
        setComments((p) => [...p, res.data?.data ?? res.data]);
      }
      if (pendingFiles.length > 0) {
        await supportApi.uploadAttachment(id, pendingFiles);
        const attRes = await supportApi.getAttachments(id);
        setAttachments(
          Array.isArray(attRes.data?.data) ? attRes.data.data : [],
        );
        setPendingFiles([]);
      }
      if (andResolve) await supportApi.resolveTicket(id);
      setReply("");
      toast.success(andResolve ? "Resolved & reply sent" : "Reply sent");
      if (andResolve) navigate(backPath);
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const onCloseTicket = async () => {
    try {
      await supportApi.closeTicket(id);
      setTicket((p) => ({ ...p, status: "Closed" }));
      setStatus("Closed");
      const actRes = await supportApi.getActivityLog(id);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Ticket closed successfully");
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  const onReopenTicket = async () => {
    try {
      await supportApi.reopenTicket(id);
      const [tRes, actRes] = await Promise.all([
        supportApi.getTicketById(id),
        supportApi.getActivityLog(id),
      ]);
      const updated = tRes.data?.data ?? tRes.data ?? {};
      setTicket(updated);
      setStatus(updated.status ?? "Open");
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Ticket reopened successfully");
    } catch {
      toast.error("Failed to reopen ticket");
    }
  };

  // When department changes: reload categories. Clearing is handled by the onChange handler.
  useEffect(() => {
    if (!departmentId) { setCategories([]); return; }
    supportApi.getCategories({ departmentId })
      .then((r) => {
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        setCategories(list.map((c) => ({ label: c.name, value: c.id })));
      })
      .catch(() => {});
  }, [departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When category changes: reload subcategories. Clearing is handled by the onChange handler.
  useEffect(() => {
    if (!categoryId) { setSubCategories([]); return; }
    supportApi.getSubCategories(categoryId)
      .then((r) => {
        const list = Array.isArray(r.data?.data) ? r.data.data : [];
        setSubCategories(list.map((s) => ({ label: s.name, value: s.id })));
      })
      .catch(() => {});
  }, [categoryId]);

  useEffect(() => {
    if (!departmentId) return;
    supportApi.getAgentsByDepartment(departmentId)
      .then((a) => {
        const list = Array.isArray(a.data?.data) ? a.data.data : [];
        setAgents(
          list.map((ag) => {
            const name = `${ag.user?.firstName ?? ""} ${ag.user?.lastName ?? ""}`.trim() || `Agent #${ag.id}`;
            const dn = ag.displayName;
            const displayName = Array.isArray(dn) ? dn[0] : dn;
            return { label: displayName ? `${name} - ${displayName}` : name, value: ag.userId };
          })
        );
      })
      .catch(() => {});
  }, [departmentId]);

  const onUpdate = async () => {
    setUpdating(true);
    try {
      await supportApi.updateTicket(id, {
        status,
        priority,
        assignedTo,
        departmentId,
        categoryId,
        subcategoryId,
      });
      const [tRes, actRes] = await Promise.all([
        supportApi.getTicketById(id),
        supportApi.getActivityLog(id),
      ]);
      const updated = tRes.data?.data ?? tRes.data ?? {};
      setTicket(updated);
      setStatus(updated.status ?? status);
      setPriority(updated.priority ?? priority);
      setAssignedTo(updated.assignedTo ?? assignedTo);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Ticket updated");
    } catch {
      toast.error("Failed to update ticket");
    } finally {
      setUpdating(false);
    }
  };

  if (!ticket)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>Loading ticket…</div>
    );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Button
          icon="pi pi-arrow-left"
          label="Back"
          outlined
          severity="secondary"
          size="small"
          onClick={() => navigate(backPath)}
        />
        <h1 className="ess-page-title" style={{ margin: 0 }}>
          {ticket.ticketNo} — {ticket.subject}
        </h1>
        <StatusTag value={ticket.status} />
        {ticket.status === "Resolved" && (
          <Button
            label="Close Ticket"
            icon="pi pi-lock"
            severity="danger"
            size="small"
            onClick={onCloseTicket}
            style={{ marginLeft: "auto" }}
          />
        )}
        {["Resolved", "Closed"].includes(ticket.status) && (
          <Button
            label="Reopen Ticket"
            icon="pi pi-refresh"
            severity="warning"
            size="small"
            onClick={onReopenTicket}
            style={{ marginLeft: ticket.status === "Resolved" ? 8 : "auto" }}
          />
        )}
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 290px", gap: 20 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <EssCard title="Ticket Details" icon="pi-file-edit">
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "var(--navy)",
                marginBottom: 12,
              }}
            >
              {ticket.subject}
            </div>
            <p
              style={{
                fontSize: 13,
                color: "#1a2a3a",
                lineHeight: 1.7,
                marginBottom: 14,
              }}
            >
              {ticket.description}
            </p>
            <Divider />
            <div className="meta-grid">
              <div className="meta-item">
                <label>Category</label>
                <span>{ticket.category?.name ?? ticket.category ?? "—"}</span>
              </div>
              <div className="meta-item">
                <label>Sub-Category</label>
                <span>{ticket.subcategory?.name ?? "—"}</span>
              </div>
              <div className="meta-item">
                <label>Raised By</label>
                <span>
                  {ticket.requester
                    ? `${ticket.requester.firstName} ${ticket.requester.lastName}`
                    : (ticket.raisedBy ?? "—")}
                </span>
              </div>
              <div className="meta-item">
                <label>Assigned To</label>
                <span>
                  {(() => {
                    if (!ticket.assignedAgent) return ticket.assignedTo ?? '—';
                    const name = `${ticket.assignedAgent.firstName} ${ticket.assignedAgent.lastName}`.trim();
                    const dn = ticket.assignedAgent?.agentProfile?.displayName;
                    const displayName = Array.isArray(dn) ? dn[0] : dn;
                    return displayName ? `${name} - ${displayName}` : (name || (ticket.assignedTo ?? '—'));
                  })()}
                </span>
              </div>
              <div className="meta-item">
                <label>Raised On</label>
                <span>
                  {ticket.createdAt
                    ? new Date(ticket.createdAt).toLocaleString()
                    : "—"}
                </span>
              </div>
              <div className="meta-item">
                <label>Due By</label>
                <span style={{ color: "var(--orange)", fontWeight: 700 }}>
                  {ticket.slaDueAt
                    ? new Date(ticket.slaDueAt).toLocaleString()
                    : "—"}
                </span>
              </div>
              <div className="meta-item">
                <label>Priority</label>
                <span>
                  <PriorityTag value={ticket.priority} />
                </span>
              </div>
              <div className="meta-item">
                <label>Status</label>
                <span>
                  <StatusTag value={ticket.status} />
                </span>
              </div>
              {Array.isArray(ticket.watcherDetails) && ticket.watcherDetails.length > 0 && (
                <div className="meta-item" style={{ gridColumn: "1 / -1" }}>
                  <label>Watchers</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {ticket.watcherDetails.map((w) => (
                      <div
                        key={w.id}
                        title={w.email ?? ""}
                        style={{ display: "flex", alignItems: "center", gap: 5,
                          background: "#e8f0fe", border: "1px solid #c5d5f5",
                          borderRadius: 20, padding: "4px 12px", fontSize: 11.5,
                          cursor: "default" }}>
                        <i className="pi pi-user" style={{ fontSize: 10, color: "#2563a8" }} />
                        <span style={{ fontWeight: 600, color: "#1a3a6b" }}>
                          {w.firstName} {w.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </EssCard>
          <EssCard title="Conversation & Response" icon="pi-comments">
            <CommentThread comments={comments} isPrivileged={true} />
            <Divider />

            {["Closed"].includes(ticket.status) ? (
              <div
                style={{
                  padding: 12,
                  background: "#f5f7fa",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--muted)",
                  }}
                >
                  Ticket Closed
                </div>
                <div
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}
                >
                  No further replies can be added.
                </div>
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "var(--bg)",
                    padding: "8px 12px",
                    borderBottom: "1px solid var(--border)",
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setPendingFiles((p) => [...p, ...files]);
                      e.target.value = "";
                    }}
                  />

                  <Button
                    label="Attach"
                    icon="pi pi-paperclip"
                    outlined
                    severity="secondary"
                    size="small"
                    onClick={() => fileInputRef.current.click()}
                  />

                  <Button
                    label={isInternal ? "Internal Note ✓" : "Internal Note"}
                    icon="pi pi-lock"
                    outlined
                    severity={isInternal ? "warning" : "secondary"}
                    size="small"
                    onClick={() => setIsInternal((p) => !p)}
                  />
                </div>

                <InputTextarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    border: "none",
                    borderRadius: 0,
                    padding: "12px 14px",
                  }}
                  placeholder={`Type your ${isInternal ? "internal note" : "response"}…`}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <Button
                    label="Send & Resolve"
                    icon="pi pi-check"
                    loading={sending}
                    style={{ background: "var(--green)", border: "none" }}
                    onClick={() => onSendReply(true)}
                  />
                  <Button
                    label="Send Reply"
                    icon="pi pi-send"
                    loading={sending}
                    style={{ background: "var(--blue)", border: "none" }}
                    onClick={() => onSendReply(false)}
                  />
                </div>
              </div>
            )}
          </EssCard>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {["Resolved", "Closed"].includes(ticket.status) && (
            <EssCard title="Ticket Management" icon="pi-sliders-h">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  This ticket is <strong>{ticket.status}</strong>. You can reopen it to resume work.
                </div>
                <Button
                  label="Reopen Ticket"
                  icon="pi pi-refresh"
                  severity="warning"
                  style={{ justifyContent: "center" }}
                  onClick={onReopenTicket}
                />
              </div>
            </EssCard>
          )}

          {!["Resolved", "Closed"].includes(ticket.status) && (
            <EssCard title="Ticket Management" icon="pi-sliders-h">
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Status
                  </label>
                  <Dropdown
                    value={status}
                    options={STATUS_OPT}
                    onChange={(e) => setStatus(e.value)}
                    style={{ width: "100%" }}
                    filter
                    filterPlaceholder="Search status…"
                  />
                </div>
                {["On Hold", "Pending"].includes(status) && (
                  <div>
                    <label
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      Reason for {status}
                    </label>
                    <InputTextarea
                      value={holdReason}
                      onChange={(e) => setHoldReason(e.target.value)}
                      rows={3}
                      placeholder={`Explain why ticket is ${status}...`}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Department
                  </label>
                  <Dropdown
                    value={departmentId}
                    options={departments}
                    onChange={(e) => { setDepartmentId(e.value); setAssignedTo(null); setCategoryId(null); setSubcategoryId(null); setSubCategories([]); }}
                    placeholder="Change department…"
                    style={{ width: "100%" }}
                    filter
                    filterPlaceholder="Search department…"
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Category
                  </label>
                  <Dropdown
                    value={categoryId}
                    options={categories}
                    onChange={(e) => { setCategoryId(e.value); setSubcategoryId(null); }}
                    placeholder="Select category…"
                    style={{ width: "100%" }}
                    showClear
                    filter
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Sub-Category
                  </label>
                  <Dropdown
                    value={subcategoryId}
                    options={subCategories}
                    onChange={(e) => setSubcategoryId(e.value)}
                    placeholder="Select sub-category…"
                    disabled={!categoryId || subCategories.length === 0}
                    style={{ width: "100%" }}
                    showClear
                    filter
                    filterPlaceholder="Search sub-category…"
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Assign To
                  </label>
                  <Dropdown
                    value={assignedTo}
                    options={agents}
                    onChange={(e) => setAssignedTo(e.value)}
                    placeholder="Select agent…"
                    style={{ width: "100%" }}
                    filter
                    filterPlaceholder="Search agent…"
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Priority
                  </label>
                  <Dropdown
                    value={priority}
                    options={PRIORITY_OPT}
                    onChange={(e) => setPriority(e.value)}
                    style={{ width: "100%" }}
                    filter
                    filterPlaceholder="Search priority…"
                  />
                </div>
                <Button
                  label="Update Ticket"
                  loading={updating}
                  style={{
                    background: "var(--blue)",
                    border: "none",
                    justifyContent: "center",
                    marginTop: 4,
                  }}
                  onClick={onUpdate}
                />
              </div>
            </EssCard>
          )}

          {/* ── Approval Section ── */}
          {!["Resolved", "Closed"].includes(ticket.status) && (
            <EssCard title="Request Approval" icon="pi-send">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Select Users for Approval
                  </label>
                  <MultiSelect
                    value={approvalUserIds}
                    options={allUsers}
                    onChange={(e) => setApprovalUserIds(e.value)}
                    placeholder="Select users…"
                    filter
                    filterPlaceholder="Search users…"
                    maxSelectedLabels={0}
                    selectedItemsLabel={
                      approvalUserIds.length === 1
                        ? "1 user selected"
                        : `${approvalUserIds.length} users selected`
                    }
                    style={{ width: "100%" }}
                    itemTemplate={(opt) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar
                          label={opt.label.substring(0, 2).toUpperCase()}
                          shape="circle"
                          size="small"
                          style={{ background: "var(--blue)", color: "#fff", fontSize: 10, width: 26, height: 26 }}
                        />
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{opt.label}</div>
                      </div>
                    )}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Approval Request Comment
                  </label>
                  <InputTextarea
                    value={approvalComment}
                    onChange={(e) => setApprovalComment(e.target.value)}
                    rows={3}
                    style={{ width: "100%" }}
                    placeholder="Describe what approval is needed…"
                  />
                </div>
                <Button
                  label="Send Approval Request"
                  icon="pi pi-send"
                  loading={sendingApproval}
                  style={{ background: "var(--blue)", border: "none", justifyContent: "center" }}
                  onClick={onRequestApproval}
                />
              </div>
            </EssCard>
          )}

          <EssCard title="SLA Breach Risk" icon="pi-exclamation-triangle">
            {["On Hold", "Pending"].includes(ticket.status) && (
              <div style={{ marginBottom: 10, padding: "8px 12px", background: "#e0f2f1",
                border: "1px solid #80cbc4", borderRadius: 6, fontSize: 12, color: "#00695c", fontWeight: 700 }}>
                ⏸ SLA Paused — Ticket is {ticket.status}
              </div>
            )}
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 30,
                  fontWeight: 900,
                  color: ticket.slaBreach ? "var(--red)" : "var(--orange)",
                }}
              >
                {ticket.slaRemaining ?? "—"}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>
                Remaining to breach
              </div>
              <ProgressBar
                value={ticket.slaPct ?? 0}
                showValue={false}
                style={{ height: 10, borderRadius: 20, margin: "10px 0" }}
                color={ticket.slaBreach ? "var(--red)" : "var(--orange)"}
              />
              <div style={{ fontSize: 11, color: "var(--muted)" }}>
                Due:{" "}
                {ticket.slaDueAt
                  ? new Date(ticket.slaDueAt).toLocaleString()
                  : "—"}
              </div>
            </div>
          </EssCard>

          <EssCard title="Attachments" icon="pi-paperclip">
            {attachments.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                No attachments
              </div>
            ) : (
              attachments.map((att) => (
                <div
                  key={att.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    marginBottom: 6,
                  }}
                >
                  <i
                    className="pi pi-file"
                    style={{ color: "var(--red)", fontSize: 18 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {att.fileName}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      {att.fileSize}
                    </div>
                  </div>
                  <i
                    className="pi pi-eye"
                    style={{
                      color: "var(--green)",
                      cursor: "pointer",
                      fontSize: 16,
                      flexShrink: 0,
                      marginRight: 6,
                    }}
                    onClick={() =>
                      supportApi
                        .downloadAttachment(id, att.id)
                        .then((res) => {
                          const blob = new Blob([res.data], {
                            type: att.mimeType || "application/octet-stream",
                          });
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 10000);
                        })
                        .catch(() => toast.error("Preview failed"))
                    }
                  />
                  <i
                    className="pi pi-download"
                    style={{
                      color: "var(--blue)",
                      cursor: "pointer",
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                    onClick={() =>
                      supportApi
                        .downloadAttachment(id, att.id)
                        .then((res) => {
                          const blob = new Blob([res.data], {
                            type: att.mimeType || "application/octet-stream",
                          });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = att.fileName;
                          a.click();
                          URL.revokeObjectURL(url);
                        })
                        .catch(() => toast.error("Download failed"))
                    }
                  />
                </div>
              ))
            )}
          </EssCard>

          <EssCard title="Activity Log" icon="pi-history">
            <Timeline
              value={activityLog}
              content={(item) => (
                <div>
                  <div style={{ fontSize: 12 }}>{item.action}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    {item.createdAt}
                  </div>
                </div>
              )}
              marker={(item) => (
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: item.color ?? "var(--blue)",
                    flexShrink: 0,
                  }}
                />
              )}
              style={{ fontSize: 12 }}
            />
          </EssCard>

          <EssCard title="Requester Info" icon="pi-user">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <Avatar
                label={
                  ticket.requester
                    ? `${ticket.requester.firstName?.[0] ?? ""}${ticket.requester.lastName?.[0] ?? ""}`.toUpperCase()
                    : (ticket.raisedBy?.substring(0, 2).toUpperCase() ?? "?")
                }
                shape="circle"
                size="large"
                style={{
                  background: "var(--blue)",
                  color: "#fff",
                  fontWeight: 700,
                }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {ticket.requester
                    ? `${ticket.requester.firstName} ${ticket.requester.lastName}`
                    : (ticket.raisedBy ?? "—")}
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>
                  {ticket.requester?.email ?? ticket.raisedByEmail ?? "—"}
                </div>
              </div>
            </div>
            {[
              [
                "Department",
                ticket.requester?.Department?.title ?? ticket.raisedByDept,
              ],
              [
                "Designation",
                ticket.requester?.Designation?.title ??
                  ticket.raisedByDesignation,
              ],
              ["Tickets Raised", ticket.ticketsRaised ?? "—"],
              [
                "Last Ticket",
                ticket.lastTicketAt
                  ? new Date(ticket.lastTicketAt).toLocaleString()
                  : "—",
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  fontSize: 11.5,
                  color: "var(--muted)",
                  marginBottom: 4,
                }}
              >
                {k}: <strong style={{ color: "#1a2a3a" }}>{v ?? "—"}</strong>
              </div>
            ))}
          </EssCard>
        </div>
      </div>
    </div>
  );
};

export default AgentWork;
